import { Box } from '@material-ui/core';
import { LenderName } from '@nemuru-eng/js-api-client';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { getInsuranceSelectOptions } from '@/components/modules/PricingsList/_shared/constants/getInsuranceSelectOptions';
import { getProgramSelectOptions } from '@/components/modules/PricingsList/_shared/constants/getProgramSelectOptions';
import { InclusionFilter } from '@/components/modules/PricingsList/_shared/constants/InclusionFilterSelectOptions';
import { PaymentMethodSelectOptions } from '@/components/modules/PricingsList/_shared/constants/PaymentMethodSelectOptions';
import { ALL_PAYMENT_METHODS } from '@/components/modules/PricingsList/_shared/schemas/pricingsForm.schema';
import { PRICINGLESS_PAYMENT_METHODS } from '@/components/modules/PricingsList/components/Filters/components/LendersList/constants/constants';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { UserEntity } from '@/entities/UserEntity';

interface usePricingsFiltersProps {
  userEntity: UserEntity;
}

export const usePricingsFilters = ({ userEntity }: usePricingsFiltersProps) => {
  const { watch } = useFormContext();

  const {
    state: { pricingsListEntity },
    actions: { updateOperationsContext },
  } = useOperationsContext();

  const [selectedLender, setSelectedLender] = useState<LenderName | undefined>(undefined);
  const [searchPricing, setSearchPricing] = useState<string | undefined>(undefined);
  const [resetAllTogglers, setResetAllTogglers] = useState(false);
  const { setValue } = useFormContext();

  const filteredPricingsByPaymentMethod = pricingsListEntity?.getPricingsByPaymentMethodFilter();

  const paymentMethodWatcher = watch('paymentMethod');
  const insurancesWatcher = watch('insurances');
  const programsWatcher = watch('programs');
  const gracePeriodWatcher = watch('gracePeriod');
  const setupFeeWatcher = watch('setupFee');
  const previousPaymentMethod = useRef(paymentMethodWatcher);

  useEffect(() => {
    if (resetAllTogglers) {
      setResetAllTogglers(false);
    }
  }, [resetAllTogglers]);

  useEffect(() => {
    if (paymentMethodWatcher !== previousPaymentMethod.current) {
      previousPaymentMethod.current = paymentMethodWatcher;
      resetAllFilters();
    }
    if (pricingsListEntity) {
      updateOperationsContext({
        pricingsListEntity: pricingsListEntity.setFilters({
          search: searchPricing,
          paymentMethod: paymentMethodWatcher,
          insurances: insurancesWatcher,
          lender: selectedLender,
          gracePeriod: gracePeriodWatcher,
          setupFee: setupFeeWatcher,
          programs: programsWatcher,
        }),
      });
    }
  }, [
    paymentMethodWatcher,
    insurancesWatcher,
    programsWatcher,
    gracePeriodWatcher,
    selectedLender,
    setupFeeWatcher,
    searchPricing,
  ]);

  const resetAllFilters = () => {
    setSelectedLender(undefined);
    setValue('gracePeriod', InclusionFilter.ALL);
    setValue('setupFee', InclusionFilter.ALL);
    setValue('programs', undefined);
    setValue('insurances', undefined);
    setResetAllTogglers(true);
    setSearchPricing(undefined);
  };

  const anyPricingWithGracePeriod = filteredPricingsByPaymentMethod?.some((pricing) =>
    pricing.ranges.find((range) => range.gracePeriod),
  );
  const anyPricingWithSetupFee = filteredPricingsByPaymentMethod?.some((pricing) =>
    pricing.ranges.find((range) => range.setupFee),
  );

  const availableInsurances = pricingsListEntity?.getAvailableInsurancesByPricings();
  const InsuranceSelectOptions = availableInsurances ? getInsuranceSelectOptions(availableInsurances) : [];

  const availablePrograms = pricingsListEntity?.getProgramsByPricings();
  const ProgramSelectOptions = availablePrograms ? getProgramSelectOptions(availablePrograms) : [];

  const availablePaymentMethodsOptions = PaymentMethodSelectOptions(
    userEntity.allowedPaymentMethodTypes.filter(
      (paymentMethod) => !PRICINGLESS_PAYMENT_METHODS.includes(paymentMethod),
    ),
  );

  const PaymentMethodsSelectOptions = [
    ...(availablePaymentMethodsOptions.length > 1
      ? [
          {
            value: ALL_PAYMENT_METHODS,
            display: (
              <Box display={'flex'} flexDirection={'row'} gridGap={8} alignItems={'center'}>
                <Icon size={18}>{'done_all'}</Icon>
                <Text tId={'ALL_PAYMENT_METHODS'} />
              </Box>
            ),
          },
        ]
      : []),
    ...availablePaymentMethodsOptions,
  ];

  return {
    userEntity,
    selectedLender,
    resetAllTogglers,
    setSelectedLender,
    anyPricingWithGracePeriod,
    anyPricingWithSetupFee,
    InsuranceSelectOptions,
    ProgramSelectOptions,
    PaymentMethodsSelectOptions,
    setSearchPricing,
  };
};
